import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
  Badge,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  Tooltip,
  Button,
  Collapse
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { ValidationResult, ValidationError } from '../validation/analytics-config.validator';

export interface ConfigValidationPanelProps {
  validationResult: ValidationResult | null;
  isValidating: boolean;
  onValidate?: () => void;
  onCopyResults?: () => void;
  className?: string;
}

export const ConfigValidationPanel: React.FC<ConfigValidationPanelProps> = ({
  validationResult,
  isValidating,
  onValidate,
  onCopyResults,
  className
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['errors']));

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const handleCopyResults = () => {
    if (validationResult && onCopyResults) {
      onCopyResults();
    }
  };

  const renderValidationItems = (items: ValidationError[], icon: React.ReactNode, emptyMessage: string) => {
    if (items.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <List dense>
        {items.map((item, index) => (
          <ListItem key={`${item.code}-${index}`} alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 32 }}>
              {icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="inherit">
                    {item.message}
                  </Typography>
                  <Chip 
                    label={item.code} 
                    size="small" 
                    variant="outlined" 
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
              }
              secondary={
                <Box sx={{ mt: 0.5 }}>
                  <Typography component="span" variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                    {item.path}
                  </Typography>
                  {item.suggestion && (
                    <Typography component="span" variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', display: 'block', color: '#f97316' }}>
                      💡 {item.suggestion}
                    </Typography>
                  )}
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>
    );
  };

  if (!validationResult && !isValidating) {
    return (
      <Paper className={className} sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          No validation results available. Click validate to check configuration.
        </Typography>
        {onValidate && (
          <Button
            startIcon={<RefreshIcon />}
            onClick={onValidate}
            sx={{ mt: 1 }}
            variant="outlined"
            size="small"
          >
            Validate Configuration
          </Button>
        )}
      </Paper>
    );
  }

  if (isValidating) {
    return (
      <Paper className={className} sx={{ p: 2 }}>
        <Alert severity="info">
          <AlertTitle>Validating Configuration...</AlertTitle>
          Please wait while we check your configuration for errors and best practices.
        </Alert>
      </Paper>
    );
  }

  if (!validationResult) {
    return null;
  }

  const { isValid, errors, warnings, info } = validationResult;

  return (
    <Paper className={className} sx={{ overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isValid ? (
              <CheckCircleIcon color="success" />
            ) : (
              <ErrorIcon color="error" />
            )}
            <Typography variant="h6">
              Configuration {isValid ? 'Valid' : 'Invalid'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onCopyResults && (
              <Tooltip title="Copy results to clipboard">
                <IconButton size="small" onClick={handleCopyResults}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            )}
            {onValidate && (
              <Tooltip title="Re-validate configuration">
                <IconButton size="small" onClick={onValidate}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Summary badges */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          {errors.length > 0 && (
            <Badge badgeContent={errors.length} color="error">
              <Chip
                icon={<ErrorIcon />}
                label="Errors"
                color="error"
                variant={expandedSections.has('errors') ? 'filled' : 'outlined'}
                onClick={() => toggleSection('errors')}
                clickable
              />
            </Badge>
          )}
          {warnings.length > 0 && (
            <Badge badgeContent={warnings.length} color="warning">
              <Chip
                icon={<WarningIcon />}
                label="Warnings"
                color="warning"
                variant={expandedSections.has('warnings') ? 'filled' : 'outlined'}
                onClick={() => toggleSection('warnings')}
                clickable
              />
            </Badge>
          )}
          {info.length > 0 && (
            <Badge badgeContent={info.length} color="info">
              <Chip
                icon={<InfoIcon />}
                label="Suggestions"
                color="info"
                variant={expandedSections.has('info') ? 'filled' : 'outlined'}
                onClick={() => toggleSection('info')}
                clickable
              />
            </Badge>
          )}
        </Box>
      </Box>

      {/* Results sections */}
      <Box>
        {/* Errors section */}
        {errors.length > 0 && (
          <Accordion expanded={expandedSections.has('errors')} onChange={() => toggleSection('errors')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" />
                <Typography>Errors ({errors.length})</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {renderValidationItems(
                errors,
                <ErrorIcon color="error" fontSize="small" />,
                'No errors found'
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Warnings section */}
        {warnings.length > 0 && (
          <Accordion expanded={expandedSections.has('warnings')} onChange={() => toggleSection('warnings')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" />
                <Typography>Warnings ({warnings.length})</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {renderValidationItems(
                warnings,
                <WarningIcon color="warning" fontSize="small" />,
                'No warnings found'
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Suggestions section */}
        {info.length > 0 && (
          <Accordion expanded={expandedSections.has('info')} onChange={() => toggleSection('info')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="info" />
                <Typography>Suggestions ({info.length})</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {renderValidationItems(
                info,
                <InfoIcon color="info" fontSize="small" />,
                'No suggestions available'
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Success state */}
        {isValid && errors.length === 0 && warnings.length === 0 && info.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Alert severity="success">
              <AlertTitle>Perfect Configuration!</AlertTitle>
              Your analytics configuration passes all validation checks and follows best practices.
            </Alert>
          </Box>
        )}
      </Box>
    </Paper>
  );
}; 